import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useAuth } from "@/hooks/useAuth";
import ProfileForm from "./components/ProfileForm";

export default function SettingsPage() {
  const { user, profile, loading } = useAuth();

  return (
    <div className="min-h-screen w-full bg-background-50">
      <Navbar />

      <div className="mx-auto w-full max-w-6xl px-4 pb-20 pt-28 md:px-8 md:pt-36">
        <div className="max-w-2xl">
          <span className="text-sm font-medium uppercase tracking-[0.2em] text-primary-500">Account</span>
          <h1 className="mt-3 font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">
            Account settings
          </h1>
          <p className="mt-3 text-base leading-relaxed text-foreground-600">
            Keep your name, phone and profile photo up to date — a complete profile builds trust with hosts and riders.
          </p>
        </div>

        {loading ? (
          <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
            <div className="h-96 w-full animate-pulse rounded-2xl bg-background-100" />
            <div className="h-64 w-full animate-pulse rounded-2xl bg-background-100" />
          </div>
        ) : !user ? (
          <div className="mt-10 flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-200 bg-background-100 px-6 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200/60 text-3xl text-foreground-500">
              <i className="ri-user-settings-line"></i>
            </span>
            <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-950">Sign in to manage your account</h3>
            <p className="mt-2 max-w-sm text-sm text-foreground-600">
              Your profile, phone number and photo are saved to your Gari account.
            </p>
            <Link
              to="/auth?redirect=/settings"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-login-circle-line"></i> Sign in
            </Link>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
            <ProfileForm />

            <aside className="space-y-4 lg:sticky lg:top-28 lg:self-start">
              <div className="rounded-2xl border border-background-200/70 bg-background-100 p-5">
                <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
                  <i className="ri-shield-check-line text-accent-400"></i> Trust signals
                </p>
                <ul className="mt-4 space-y-3 text-sm">
                  <li className="flex items-center justify-between">
                    <span className="text-foreground-600">Verified email</span>
                    <span className="inline-flex items-center gap-1 text-accent-300">
                      <i className="ri-checkbox-circle-fill"></i> Yes
                    </span>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="text-foreground-600">Phone added</span>
                    <span className={`inline-flex items-center gap-1 ${profile?.phone ? "text-accent-300" : "text-foreground-500"}`}>
                      <i className={profile?.phone ? "ri-checkbox-circle-fill" : "ri-time-line"}></i>
                      {profile?.phone ? "Yes" : "Add it"}
                    </span>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="text-foreground-600">Profile photo</span>
                    <span className={`inline-flex items-center gap-1 ${profile?.avatar_url ? "text-accent-300" : "text-foreground-500"}`}>
                      <i className={profile?.avatar_url ? "ri-checkbox-circle-fill" : "ri-time-line"}></i>
                      {profile?.avatar_url ? "Yes" : "Add it"}
                    </span>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="text-foreground-600">About you</span>
                    <span className={`inline-flex items-center gap-1 ${profile?.bio ? "text-accent-300" : "text-foreground-500"}`}>
                      <i className={profile?.bio ? "ri-checkbox-circle-fill" : "ri-time-line"}></i>
                      {profile?.bio ? "Done" : "Add it"}
                    </span>
                  </li>
                </ul>
              </div>

              <div className="rounded-2xl border border-background-200/70 bg-background-100 p-5">
                <p className="text-xs uppercase tracking-wider text-foreground-500">Signed in as</p>
                <p className="mt-1 truncate font-heading text-base font-semibold text-foreground-950">
                  {profile?.full_name || user.email}
                </p>
                <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary-500/12 px-2.5 py-1 text-xs font-medium capitalize text-primary-400">
                  <i className={profile?.role === "host" ? "ri-hand-coin-line" : "ri-steering-2-line"}></i>
                  {profile?.role || "rider"}
                </span>
              </div>

              <Link
                to="/dashboard"
                className="flex items-center justify-between rounded-2xl border border-background-200/70 bg-background-100 px-5 py-4 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500/60"
              >
                Host dashboard
                <i className="ri-arrow-right-line text-primary-500"></i>
              </Link>
            </aside>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}